<!--
Author: Alexey Usov (dax@xdax.ru, https://github.com/doubleaxe)
Please don't remove this comment if you use unmodified file
-->
<script setup lang="ts">
import {ref, computed, reactive, unref, watch, onMounted} from 'vue';
import {
    injectBlueprintModel,
    type BlueprintItemModel,
} from '@/scripts/model/store';
import {injectSettings} from '@/scripts/settings';
import {injectGameData} from '@/scripts/data';
import RecipesMenu from './recipes-menu.vue';
import RecipeSuggestionMenu from './recipe-suggestion-menu.vue';
import PlaceMenu from './place-menu.vue';
import {syncRefs, useEventListener, type MaybeElement} from '@vueuse/core';
import {
    SelectedClassType,
    useDragAndScroll,
    useItemDragAndDrop,
    useLeftPanelDragAndDrop,
    useLinkDragAndDrop,
    useOverflowScroll,
    usePointAndClick,
    useSharedBlueprintSurface,
    screenToClient,
} from '@/composables/drag-helpers';
import {useEventHook, useInspectorSelection, useLastPlacedFactory} from '@/composables';
import {injectFilter} from '@/scripts/filter';
import {Rect, type ReadonlyPointType} from '@/scripts/geometry';
import type {GameItem, GameRecipe} from '#types/game-data';
import {buildTransformStyle} from '@/scripts/util';
import {snapPointToGrid} from '@/scripts/grid';

const settings = injectSettings();
const filter = injectFilter();
const gameData = injectGameData();
const blueprintModel = injectBlueprintModel();
const blueprintSurface = ref<MaybeElement>(null);
const blueprintCollection = ref<MaybeElement>(null);
const recipesMenuElement = ref<InstanceType<typeof RecipesMenu> | null>(null);
const recipeSuggestionMenuElement = ref<InstanceType<
  typeof RecipeSuggestionMenu
> | null>(null);
const placeMenuElement = ref<InstanceType<typeof PlaceMenu> | null>(null);
//fork addition: board coordinates of the click that opened the place
//menu — the chosen factory is placed there
const placeClientPoint = ref<ReadonlyPointType | undefined>();
//fork addition: drops and suggestion placements end with a click event
//on the board; suppress the place menu for those, it is only for
//genuine empty-board clicks
let suppressPlaceMenuUntil = 0;
const {setLastPlaced} = useLastPlacedFactory();
//detects if screen was scrolled between pointer down and click, don't perform anything if was scrolled
let wasScrolled = false;

const {onStart: startDragAndScroll, onStartPan: startDragAndScrollPan} = useDragAndScroll();
useOverflowScroll(blueprintSurface);
const {
    hooks: leftPanelHooks,
    dropZoneSurfaceElem: dropZoneSurfaceElem1,
    dropZoneOriginElem: dropZoneOriginElem1,
} = useLeftPanelDragAndDrop();
const {
    dropZoneSurfaceElem: dropZoneSurfaceElem2,
    dropZoneOriginElem: dropZoneOriginElem2,
} = useLinkDragAndDrop();
const {
    hooks: itemHooks,
    dropZoneSurfaceElem: dropZoneSurfaceElem3,
    dropZoneOriginElem: dropZoneOriginElem3,
} = useItemDragAndDrop();
const {
    processSelected,
    parentElem: dropZoneOriginElem4,
    notifySelected,
} = usePointAndClick();
const {surfaceElem, originElem, updateSurface, boundingRect} =
  useSharedBlueprintSurface();
//fork addition: pressing empty board clears the inspector selection
//(item presses stopPropagation, so they never reach this handler)
const {clearSelection: clearInspectorSelection} = useInspectorSelection();

//fork fix: remember where a left press started on the board, so the
//click handler can tell a genuine click from the end of a pan — a pan
//must never fall through to clearing the selection / opening the
//click-to-place menu, even when it produced no scroll events (e.g.
//dragging against the scroll edge)
let surfacePointerDownPos: {x: number; y: number} | undefined = undefined;
function handleSurfacePointerDown(event: PointerEvent) {
    startDragAndScroll(event);
    wasScrolled = false;
    surfacePointerDownPos = {x: event.clientX, y: event.clientY};
}

//fork addition: right/middle-button press anywhere on the board starts
//a pan — the user's explicit pan gesture. Capture phase on the surface,
//so it fires before any child handler (cards, links, menus) can stop
//the press; preventDefault also stops middle-click autoscroll.
useEventListener(() => unref(blueprintSurface) as HTMLElement | null, 'pointerdown', (event: PointerEvent) => {
    if((event.button === 1) || (event.button === 2)) {
        event.preventDefault();
        startDragAndScrollPan(event);
    }
}, {capture: true});

function suppressContextMenu() {
    //the board pans with the right button; the browser context menu
    //never belongs on the canvas (the .prevent modifier does the work)
}

//fork fix: the inspector selection is cleared ONLY by a genuine click on
//empty board — and must not be the end of a pan/drag (those scroll,
//which sets wasScrolled). Clearing on pointerdown kicked the user out
//of the details panel whenever a press reached the board handlers.
//
//The empty-board test must NOT compare the click target against the
//surface/collection elements by identity: since the virtual canvas
//grew, the collection (and with it the full-size links <svg> background
//layer inside it) covers the whole viewport, so a click on empty board
//lands on that background layer, never on the surface/collection
//elements themselves — the identity test silently rejected every
//genuine empty click (no clear, no place menu). Instead, a click that
//reaches this handler counts as empty board unless it landed on board
//content: inside a factory card, on a link line, or on a link
//connector. (Menus are teleported out of the surface, so their clicks
//never reach this handler at all.)
function handleSurfaceClick(event: MouseEvent) {
    if(event.button !== 0)
        return;
    processSelected(event);
    if(wasScrolled)
        return;
    const downPos = surfacePointerDownPos;
    surfacePointerDownPos = undefined;
    if(downPos && (Math.max(Math.abs(event.clientX - downPos.x), Math.abs(event.clientY - downPos.y)) > 6))
        return;
    const target = event.target as Element | null;
    if(!target || (typeof target.closest !== 'function'))
        return;
    if(target.closest('[data-item-id], .link, .link-connector'))
        return;
    clearInspectorSelection();
    //fork addition: a genuine click on empty board opens the
    //click-to-place menu at the click point (suppressed right after
    //drops / suggestion placements, whose trailing click this also is)
    if(Date.now() < suppressPlaceMenuUntil)
        return;
    const collectionEl = unref(blueprintCollection) as HTMLElement | null;
    if(!collectionEl)
        return;
    placeClientPoint.value = screenToClient(
        collectionEl,
        Rect.assign({x: event.clientX, y: event.clientY}),
        settings.scale,
    );
    placeMenuElement.value?.activate({x: event.clientX, y: event.clientY});
}

//fork addition: factory chosen in the place menu — placed at the
//board point the menu was opened from, through the normal add path
function handlePlaceMenuPlace(factory: GameItem) {
    const point = unref(placeClientPoint);
    if(point)
        addItem(factory, point);
}

syncRefs(blueprintSurface, [
    surfaceElem,
    dropZoneSurfaceElem1,
    dropZoneSurfaceElem2,
    dropZoneSurfaceElem3,
]);
syncRefs(blueprintCollection, [
    originElem,
    dropZoneOriginElem1,
    dropZoneOriginElem2,
    dropZoneOriginElem3,
    dropZoneOriginElem4,
]);

function addItem(selected: GameItem, clientPosition: ReadonlyPointType) {
    const item = reactive(blueprintModel.addItem(selected.name));
    item.setRect(item.rect.assignPoint(snapPointToGrid(clientPosition)));
    setLastPlaced(selected.name);
    if(filter.key) {
        const preselectRecipe = item.possibleRecipeForItem(
            filter.key,
            filter.direction,
        );
        if(preselectRecipe) item.selectRecipe(preselectRecipe);
    }
}
useEventHook(leftPanelHooks.notifyDrop, (param) => {
    suppressPlaceMenuUntil = Date.now() + 400;
    addItem(param.item, param.clientRect);
});

useEventHook(notifySelected, (param) => {
    if(wasScrolled) {
        return;
    }
    //point-and-click placements end in the same board click — don't
    //let it also open the place menu
    suppressPlaceMenuUntil = Date.now() + 400;
    if(param.item.clazz == SelectedClassType.Item) {
        addItem(param.item.item as GameItem, param.clientPosition);
        param.wasHandled();
        return;
    }
    if(param.item.clazz == SelectedClassType.BlueprintItemModel) {
        const item = param.item.item as BlueprintItemModel;
        item.setRect(
            item.rect.assignPoint(snapPointToGrid(param.clientPosition)).limit(unref(boundingRect)),
        );
        param.wasHandled();
        return;
    }
});

useEventHook([itemHooks.notifyMove, itemHooks.notifyDrop], (param) => {
    const item = param.item;
    item.setRect(
        item.rect.assignPoint(snapPointToGrid(param.clientRect)).limit(unref(boundingRect)),
    );
    if(param.event == 'notifyDrop') {
        suppressPlaceMenuUntil = Date.now() + 400;
        updateSurface(blueprintModel.items);
    }
});

const scaleStyle = computed(() => {
    return {
        transform: buildTransformStyle({scale: String(settings.scale)}),
    };
});

watch(
    [() => blueprintModel.itemsGenerationNumber, () => settings.scale],
    () => {
    //on blueprint load reset scroll position
        const resetScroll = !blueprintModel.itemsGenerationNumber;
        //fork fix: a freshly loaded blueprint always opens at 100% scale,
        //framed at the top-left inset — a persisted zoom level from wheel
        //scaling otherwise hides the plan off-screen
        if(resetScroll && settings.scale != 1) {
            settings.scale = 1;
        }
        updateSurface(blueprintModel.items, resetScroll);
    },
);

function handleScale(event: WheelEvent) {
    if(settings.scrollScaleEnabled) {
        if(event.deltaY < 0) {
            settings.scale = Math.min(settings.scale + 0.1, 2);
        } else {
            settings.scale = Math.max(settings.scale - 0.1, 0.2);
        }
        event.stopPropagation();
        event.preventDefault();
    }
}

useEventListener(
    'scroll',
    () => {
        wasScrolled = true;
    },
    {capture: true, passive: true},
);
onMounted(() => {
    updateSurface(blueprintModel.items);
});

// Handle output dropped in empty space - show recipe suggestions for consumers
function handleOutputDroppedEmpty(
    productName: string,
    sourceItemId: string,
    screenPosition: ReadonlyPointType,
) {
    suppressPlaceMenuUntil = Date.now() + 400;
    recipeSuggestionMenuElement.value?.activate(
        productName,
        sourceItemId,
        screenPosition,
        'consuming',
    );
}

// Handle input dropped in empty space - show recipe suggestions for producers
function handleInputDroppedEmpty(
    productName: string,
    sourceItemId: string,
    screenPosition: ReadonlyPointType,
) {
    suppressPlaceMenuUntil = Date.now() + 400;
    recipeSuggestionMenuElement.value?.activate(
        productName,
        sourceItemId,
        screenPosition,
        'producing',
    );
}

// Handle recipe selected from suggestion menu - create factory and link
function handleRecipeSelected(
    factory: GameItem,
    recipe: GameRecipe,
    productName: string,
    sourceItemId: string,
    screenPosition: ReadonlyPointType,
    mode: 'consuming' | 'producing',
) {
    // Get the collection element for position calculation
    const collectionEl = unref(blueprintCollection) as HTMLElement | null;
    if(!collectionEl) return;

    // Convert screen position to client position (accounting for scale)
    const clientPosition = screenToClient(
        collectionEl,
        Rect.assign(screenPosition),
        settings.scale,
    );

    // Create new factory item
    const newItem = reactive(blueprintModel.addItem(factory.name));
    setLastPlaced(factory.name);

    // Position the factory at the drop location, snapped to the board grid
    newItem.setRect(
        newItem.rect.assignPoint(snapPointToGrid(clientPosition)).limit(unref(boundingRect)),
    );

    // Select the specific recipe
    newItem.selectRecipe(recipe.name);

    // Find the exact source/target item by ID
    const linkedItem = blueprintModel.itemByKey(sourceItemId);
    if(!linkedItem) return;

    if(mode === 'consuming') {
    // We created a consumer (newItem), linkedItem is the producer (source)
        const outputs = linkedItem.selectedRecipe?.visibleOutput() || [];
        for(const output of outputs) {
            if(output.name === productName) {
                // Found the source output - create link to new factory
                newItem.createLink(output);
                updateSurface(blueprintModel.items);
                return;
            }
        }
    } else {
    // Mode is 'producing', we created a producer (newItem), linkedItem is the consumer (target)
        const inputs = linkedItem.selectedRecipe?.visibleInput() || [];
        for(const input of inputs) {
            if(input.name === productName) {
                // Found the target input - link new factory's output to it.
                // The producer's output may be an abstract class io (e.g.
                // a storage's "Any Loose Product"), whose name is not the
                // concrete product — fall back to the unmaterialized
                // abstract io of the same product type, which the model's
                // link matching accepts
                const productItem = gameData.getGameItem(productName);
                const outputs = newItem.selectedRecipe?.visibleOutput() || [];
                const outputIo = outputs.find((o) => o.name === productName)
                    || outputs.find((o) => o.isAbstractClassItem
                        && !o.isMatherialized
                        && (productItem !== undefined)
                        && (o.type === productItem.type));
                if(outputIo) {
                    linkedItem.createLink(outputIo);
                }
                updateSurface(blueprintModel.items);
                return;
            }
        }
    }
}
</script>

<template>
    <div
        ref="blueprintSurface"
        class="blueprint-surface"
        @pointerdown.left="handleSurfacePointerDown($event)"
        @click="handleSurfaceClick($event)"
        @contextmenu.prevent="suppressContextMenu"
        @wheel="handleScale"
    >
        <div
            ref="blueprintCollection"
            class="blueprint-collection"
            :style="scaleStyle"
        >
            <link-draggable
                @output-dropped-empty="handleOutputDroppedEmpty"
                @input-dropped-empty="handleInputDroppedEmpty"
            />
            <recipes-menu ref="recipesMenuElement" />
            <recipe-suggestion-menu
                ref="recipeSuggestionMenuElement"
                @recipe-selected="handleRecipeSelected"
            />
            <place-menu
                ref="placeMenuElement"
                @place="handlePlaceMenuPlace"
            />
            <blueprint-links />
            <optimized-tooltip>
                <template v-for="item in blueprintModel.items" :key="item.key">
                    <blueprint-single-item
                        :item="item"
                        :parent="blueprintCollection"
                        @recipes-menu-activate="recipesMenuElement?.activate"
                    />
                </template>
            </optimized-tooltip>
        </div>
    </div>
</template>

<style scoped>
.blueprint-surface {
    transform-origin: 0 0;
    min-width: 100%;
    min-height: 100%;
    /* fork addition: empty board is draggable — advertise it */
    cursor: grab;
    /* border: 1px solid red; */
}
.blueprint-surface:active {
    cursor: grabbing;
}
/* fork addition: dark theme — lift the board surface off the near-black
   panels around it with a slightly lighter dark grey, so the canvas
   reads as its own area; the light theme keeps the current plain look */
.v-theme--dark .blueprint-surface {
    background-color: #26262b;
}
.blueprint-collection {
    transform-origin: 0 0;
    position: relative;
    overflow: visible;
    /* fork addition: visible board grid — minor lines every GRID_SIZE
       (25px), major lines every 5 cells. Sizes must match
       site/src/scripts/grid.ts.
       Alignment: the pattern is anchored to this element's own origin
       (background-position 0 0, no padding/border), and this element's
       local coordinate system IS the board coordinate system — items are
       absolutely positioned children with left/top equal to their board
       rect. So cell (0,0) is exactly board (0,0), a factory snapped to
       (0,0) sits on a grid intersection at any scroll position, and
       because the background lives on the scaled element itself it stays
       phase-locked to the content under the scale transform too.
       Do NOT reintroduce a boundingRect-derived background-position:
       that would shift the pattern off the board grid. */
    background-image:
        linear-gradient(to right, rgba(128, 128, 128, 0.5) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(128, 128, 128, 0.5) 1px, transparent 1px),
        linear-gradient(to right, rgba(128, 128, 128, 0.22) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(128, 128, 128, 0.22) 1px, transparent 1px);
    background-size:
        125px 125px,
        125px 125px,
        25px 25px,
        25px 25px;
    /* border: 1px solid green; */
}
</style>
